import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Bell,
  FileText,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  Server,
  Database,
  Radio,
  Shield,
  ShieldBan,
  GitBranch
} from 'lucide-react';
import CustomerHeader from './CustomerHeader';
import './CustomerDesign.css';
import { useOrganization } from '../context/OrganizationContext';

const AppLayout = ({ children, onLogout, headerActions }) => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const { activeOrganization } = useOrganization();

  const routeDetails = {
    '/dashboard': {
      title: 'Dashboard',
      subtitle: 'Security operations overview'
    },
    '/alerts': {
      title: 'Alerts',
      subtitle: 'Review and investigate security alerts'
    },
    '/incidents': {
      title: 'Incidents',
      subtitle: 'Correlated attack chains, behavioral signals, and baselines'
    },
    '/responses': {
      title: 'Response Center',
      subtitle: 'Approvals, playbooks, rollback, and enforcement policy'
    },
    '/devices': {
      title: 'Devices',
      subtitle: 'Tenant-scoped protected devices'
    },
    '/gateways': {
      title: 'Gateway Fleet',
      subtitle: 'Sites, enrollment, health, and response-capable agents'
    },
    '/ingestions': {
      title: 'Ingestions',
      subtitle: 'Tenant-owned ingestion activity'
    },
    '/reports': {
      title: 'Reports',
      subtitle: 'Security intelligence and reporting'
    },
    '/syslog': {
      title: 'Syslog',
      subtitle: 'Tenant-scoped collector alerts and events'
    },
    '/admin': {
      title: 'Organization Admin',
      subtitle: 'Manage the active customer workspace'
    },
    '/settings': {
      title: 'Settings',
      subtitle: 'Platform configuration'
    },
    '/support': {
      title: 'Support',
      subtitle: 'CybeDefender support resources'
    },
    '/profile-settings': {
      title: 'Profile Settings',
      subtitle: 'Manage your account'
    }
  };

  const currentRoute = routeDetails[location.pathname] || {
    title:
      location.pathname.substring(1).charAt(0).toUpperCase() +
      location.pathname.slice(2),
    subtitle: 'CybeDefender AI Security Platform'
  };

  const operations = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/alerts', label: 'Alerts', icon: Bell },
    { to: '/incidents', label: 'Incidents', icon: GitBranch },
    { to: '/responses', label: 'Response', icon: ShieldBan },
    { to: '/reports', label: 'Reports', icon: FileText }
  ];

  const workspace = [
    { to: '/ingestions', label: 'Ingestions', icon: Database },
    { to: '/syslog', label: 'Syslog', icon: Radio }
  ];

  const management = [
    { to: '/gateways', label: 'Gateways', icon: Server },
    ...(activeOrganization?.membership_role === 'admin'
      ? [{ to: '/admin', label: 'Admin', icon: Shield }]
      : [])
  ];

  const NavLink = ({ item }) => {
    const Icon = item.icon;
    const active = location.pathname === item.to;

    return (
      <Link
        to={item.to}
        className={`group flex items-center rounded-xl transition-all duration-200 ${
          collapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2.5'
        } ${
          active
            ? 'bg-cyan-400/10 text-cyan-300 ring-1 ring-cyan-400/20'
            : 'text-slate-400 hover:bg-white/5 hover:text-white'
        }`}
        title={collapsed ? item.label : undefined}
      >
        <Icon className="h-5 w-5 shrink-0" />
        {!collapsed && (
          <span className="text-sm font-medium">{item.label}</span>
        )}
      </Link>
    );
  };

  return (
    <div className="customer-app min-h-screen bg-slate-50" style={{ '--customer-header-height': '64px' }}>
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 z-50 h-px bg-slate-300/70"
        style={{ top: 'calc(var(--customer-header-height) - 1px)' }}
      />
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex flex-col border-r border-white/10 bg-[#07111f] transition-all duration-300 ${
          collapsed ? 'w-20' : 'w-64'
        }`}
      >
        <div className="flex items-center px-4" style={{ height: 'var(--customer-header-height)' }}>
          <div
            className={`flex h-10 w-full items-center ${
              collapsed ? 'justify-center' : 'justify-between'
            }`}
          >
            <div className="flex h-10 items-center gap-3 overflow-hidden">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-400/10 ring-1 ring-cyan-300/20">
                <ShieldCheck className="h-6 w-6 text-cyan-300" />
              </div>

              {!collapsed && (
                <div className="flex h-10 min-w-0 flex-col justify-center">
                  <div className="truncate text-sm font-semibold leading-4 text-white">
                    CybeDefender
                  </div>
                  <div className="truncate text-xs leading-4 text-slate-400">
                    AI Security
                  </div>
                </div>
              )}
            </div>

            {!collapsed && (
              <button
                type="button"
                onClick={() => setCollapsed(true)}
                className="mt-0 flex h-10 w-10 items-center justify-center rounded-lg p-0 text-slate-500 transition hover:bg-white/5 hover:text-white"
                aria-label="Collapse sidebar"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-5">
          {!collapsed && (
            <div className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
              Operations
            </div>
          )}

          <nav className="space-y-1">
            {operations.map((item) => (
              <NavLink key={item.to} item={item} />
            ))}
          </nav>

          <div className="my-5 border-t border-white/10" />

          {!collapsed && (
            <div className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
              Workspace
            </div>
          )}

          <nav className="space-y-1">
            {workspace.map((item) => (
              <NavLink key={item.to} item={item} />
            ))}
          </nav>

          {management.length > 0 && (
            <>
              <div className="my-5 border-t border-white/10" />

              {!collapsed && (
                <div className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                  Management
                </div>
              )}

              <nav className="space-y-1">
                {management.map((item) => (
                  <NavLink key={item.to} item={item} />
                ))}
              </nav>
            </>
          )}
        </div>

        <div className="border-t border-white/10 p-3">
          <button type="button" onClick={() => setCollapsed(value => !value)} className="flex w-full items-center justify-center gap-2 rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-white" aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            {collapsed ? <ChevronRight className="h-4 w-4"/> : <><ChevronLeft className="h-4 w-4"/><span className="text-xs">Collapse sidebar</span></>}
          </button>
        </div>
      </aside>

      <div
        className={`min-h-screen transition-all duration-300 ${
          collapsed ? 'ml-20' : 'ml-64'
        }`}
      >
        <CustomerHeader currentRoute={currentRoute} onLogout={onLogout} actions={headerActions} />

        <main className="customer-content mx-auto w-full max-w-[1600px] p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
